/**
 * Product Detail Page
 * Full product view with image gallery, variant selection, specifications,
 * compare toggle, and shortlist-to-project functionality.
 */

import type { CatalogProductImage } from "@shared/types";
import {
	ArrowLeft,
	Check,
	ChevronRight,
	GitCompare,
	IndianRupee,
	Layers,
	Plus,
	ShoppingCart,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { VendorTypeahead } from "@/features/catalog/components/vendor-typeahead";
import {
	type CatalogProduct,
	getCatalogProduct,
	type ProductVariant,
} from "@/features/catalog/services/catalog-api.service";
import { formatUom, UOM_VALUES } from "@/features/catalog/utils/uom";
import {
	addToShortlist,
	getDesignerProjects,
	getMyProjects,
	getProjectsDashboard,
	type Project,
} from "@/features/projects/services/projects-api.service";
import {
	addQuote,
	type VendorIdentity,
} from "@/features/projects/services/quotes-api.service";
import { Eyebrow, FloatPanel, StudioButton } from "@/features/studio/theme";
import { formatCalendarDate, formatPricePaisa } from "@/lib/format";
import { cn } from "@/lib/utils";

// ============================================================================
// HELPERS
// ============================================================================

const TIER_STYLES: Record<string, { label: string; className: string }> = {
	standard: {
		label: "Standard",
		className: "bg-dashboard-divider text-dashboard-ink-2",
	},
	premium: {
		label: "Premium",
		className: "bg-dashboard-pill-info-bg text-dashboard-info-strong",
	},
	luxury: {
		label: "Luxury",
		className: "bg-dashboard-pill-awaiting-bg text-dashboard-pill-awaiting",
	},
};

const COMPARE_STORAGE_KEY = "catalog_compare_ids";

function getCompareIds(): string[] {
	try {
		const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

function setCompareIds(ids: string[]): void {
	localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(ids));
}

// ============================================================================
// LOADING SKELETON
// ============================================================================

function ProductDetailSkeleton(): React.ReactElement {
	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-5xl space-y-6">
				{/* Breadcrumb skeleton */}
				<Skeleton className="h-4 w-64" />

				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					{/* Image gallery skeleton */}
					<div className="space-y-3">
						<Skeleton className="aspect-square w-full rounded-lg" />
						<div className="flex gap-2">
							<Skeleton className="h-16 w-16 rounded-md" />
							<Skeleton className="h-16 w-16 rounded-md" />
							<Skeleton className="h-16 w-16 rounded-md" />
						</div>
					</div>

					{/* Info skeleton */}
					<div className="space-y-4">
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-8 w-3/4" />
						<Skeleton className="h-6 w-20 rounded-full" />
						<Skeleton className="h-8 w-40" />
						<Skeleton className="h-20 w-full" />
						<div className="flex gap-3">
							<Skeleton className="h-10 w-36" />
							<Skeleton className="h-10 w-44" />
						</div>
					</div>
				</div>

				{/* Specifications skeleton */}
				<FloatPanel elevation={1} padding={24}>
					<div className="space-y-3">
						<Skeleton className="h-6 w-32" />
						<Skeleton className="h-5 w-full" />
						<Skeleton className="h-5 w-full" />
						<Skeleton className="h-5 w-3/4" />
					</div>
				</FloatPanel>
			</div>
		</div>
	);
}

// ============================================================================
// NOT FOUND STATE
// ============================================================================

function ProductNotFound(): React.ReactElement {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-5xl space-y-8">
				<StudioButton
					variant="quiet"
					size="sm"
					onClick={() => navigate(-1)}
					icon={ArrowLeft}
				>
					{t("common.back", "Back")}
				</StudioButton>
				<FloatPanel elevation={1} padding={48}>
					<div className="text-center space-y-4">
						<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-dashboard-warm-350">
							<Layers className="h-8 w-8 text-[var(--color-dashboard-muted)]" />
						</div>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 600,
								fontSize: 22,
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("productDetail.notFound", "Product not found")}
						</h2>
						<p
							style={{
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
								fontFamily: "var(--font-body)",
							}}
						>
							{t(
								"productDetail.notFoundDesc",
								"The product you are looking for does not exist or has been removed.",
							)}
						</p>
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => navigate(-1)}
						>
							{t("common.goBack", "Go Back")}
						</StudioButton>
					</div>
				</FloatPanel>
			</div>
		</div>
	);
}

// ============================================================================
// IMAGE GALLERY
// ============================================================================

interface ImageGalleryProps {
	images: CatalogProductImage[];
	/**
	 * What to announce for an image nobody has described.
	 *
	 * The product name, which is what every other catalogue surface falls back
	 * to. Most stored images have no `alt` yet -- the backfill wrote null rather
	 * than guessing, and seed images are deliberately not being described -- so
	 * this is the common path, not the edge case.
	 */
	fallbackAlt: string;
}

/**
 * The detail page's gallery.
 *
 * THIS IS WHERE STORED ALT TEXT LANDS. The single-product detail response is
 * the one projection carrying full image objects (the plan's KTD4), and this is
 * what renders them -- browse and the designer tabs read list projections that
 * still send URLs, so they have nothing to render and are deliberately
 * untouched.
 *
 * It previously announced the literal string "Product" for every image on every
 * product. The plan's Problem Frame says all three catalogue sites pass
 * `alt={product.name}`; this one did not, and it is the largest image on the
 * page.
 */
function ImageGallery({
	images,
	fallbackAlt,
}: ImageGalleryProps): React.ReactElement {
	const [selectedIndex, setSelectedIndex] = useState(0);

	/*
	 * CLAMPED ON READ rather than reset in an effect.
	 *
	 * The selection can outlive the gallery: pick thumbnail 3, switch to a
	 * variant carrying two images, and the index points past the end -- the main
	 * image then rendered with no `src` at all. An effect resetting to 0 was the
	 * other option, and this is the smaller change: the effect here had an empty
	 * dependency array and only ever ran on mount, so "keep the position" is the
	 * behaviour that has actually been shipping. This repairs the out-of-range
	 * case without altering it.
	 */
	const activeIndex = Math.min(selectedIndex, Math.max(images.length - 1, 0));

	if (images.length === 0) {
		return (
			<div className="flex aspect-square items-center justify-center rounded-lg border bg-muted">
				<Layers className="h-16 w-16 text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{/* Primary image */}
			<div className="overflow-hidden rounded-lg border bg-dashboard-paper">
				<img
					src={images[activeIndex]?.url}
					alt={images[activeIndex]?.alt || fallbackAlt}
					className="aspect-square w-full object-contain"
				/>
			</div>

			{/* Thumbnail row */}
			{images.length > 1 && (
				<div className="flex gap-2 overflow-x-auto">
					{images.slice(0, 5).map((img, idx) => (
						<button
							type="button"
							key={img.url}
							onClick={() => setSelectedIndex(idx)}
							className={cn(
								"h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 transition-colors",
								activeIndex === idx
									? "border-primary"
									: "border-transparent hover:border-muted-foreground/30",
							)}
						>
							<img
								src={img.url}
								/*
								 * The stored description when there is one. A thumbnail
								 * announcing "Thumbnail 3" tells a screen-reader user the
								 * position they already know and nothing about the picture
								 * they are choosing between.
								 */
								alt={img.alt || `${fallbackAlt} — view ${idx + 1}`}
								className="h-full w-full object-cover"
							/>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

// ============================================================================
// SHORTLIST MODAL
// ============================================================================

interface ShortlistModalProps {
	product: CatalogProduct;
	selectedVariant: ProductVariant | null;
	onClose: () => void;
}

function ShortlistModal({
	product,
	selectedVariant,
	onClose,
}: ShortlistModalProps): React.ReactElement {
	const { t } = useTranslation();
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [quantity, setQuantity] = useState<string>("");
	const [notes, setNotes] = useState("");
	const [isLoadingProjects, setIsLoadingProjects] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const userType = useAuthStore.getState().getUserType();
	// Contractors too, not designers only. They have the strongest real-world
	// vendor relationships and were excluded from this flow entirely. The server
	// enforces the same pair; this only decides whether the form is shown.
	const canAddQuotes = userType === "designer" || userType === "contractor";

	// The SAME units products are priced in. The old list was a third private
	// vocabulary -- `mtr`, `Qt` -- that mapped to nothing, so a quote price could
	// not be compared with a catalogue price at all, and `Qt` was ambiguous
	// between quintal and quart even to a human.
	const UNIT_OPTIONS = UOM_VALUES;

	/**
	 * Quote rows.
	 *
	 * One to start, not three. The old form hardcoded exactly three slots with
	 * fixed tags, which is why the homeowner's comparison grid was
	 * `repeat(3, 1fr)` and why the API grew a three-quote assumption. There is no
	 * right number: some products get one quote, some get six.
	 *
	 * No `rating` field. It was seeded 4.8 / 4.5 / 4.9 and meant nothing -- there
	 * was no vendor entity for it to accumulate against. A vendor is rated after
	 * an approved engagement now, which is the first moment the system observes
	 * anything a score could be based on.
	 *
	 * No "Designer's pick" default either: a contractor can add quotes, and a
	 * contractor-sourced quote labelled as the designer's recommendation
	 * misattributes it at the exact moment money is committed.
	 */
	type QuoteDraft = {
		key: string;
		vendor: VendorIdentity | null;
		price: string;
		unit: string;
		leadTimeDays: string;
		tag: string;
	};

	const newRow = (): QuoteDraft => ({
		key: `q-${Math.random().toString(36).slice(2)}`,
		vendor: null,
		price: "",
		unit: product.uom || "piece",
		leadTimeDays: "",
		tag: "",
	});

	const [quotes, setQuotes] = useState<QuoteDraft[]>([newRow()]);

	const updateQuote = (index: number, patch: Partial<QuoteDraft>) => {
		setQuotes((prev) =>
			prev.map((q, i) => (i === index ? { ...q, ...patch } : q)),
		);
	};

	const hasVariants = product.variants && product.variants.length > 0;
	const needsVariantSelection = hasVariants && !selectedVariant;

	useEffect(() => {
		async function fetchProjects(): Promise<void> {
			setIsLoadingProjects(true);
			try {
				const userType = useAuthStore.getState().getUserType();
				let allProjects: Project[];

				if (userType === "homeowner") {
					allProjects = await getMyProjects();
				} else if (userType === "designer") {
					allProjects = await getDesignerProjects();
				} else {
					// Contractors use the dashboard API and map to Project shape
					const dashboard = await getProjectsDashboard();
					allProjects = dashboard.projects.map((p) => ({
						id: p.id,
						homeownerId: "",
						title: p.title,
						scope: (p.scope as "full_home" | "partial") || "full_home",
						status: p.status as Project["status"],
						budgetMin: p.budgetMin ?? undefined,
						budgetMax: p.budgetMax ?? undefined,
						timelineWeeks: p.timelineWeeks ?? undefined,
						createdAt: p.createdAt,
						updatedAt: p.updatedAt,
					}));
				}

				setProjects(allProjects);
				if (allProjects.length > 0) {
					setSelectedProjectId(allProjects[0].id);
				}
			} catch {
				toast.error(
					t("productDetail.failedToLoadProjects", "Failed to load projects"),
				);
			} finally {
				setIsLoadingProjects(false);
			}
		}
		fetchProjects();
	}, [t]);

	async function handleSubmit(): Promise<void> {
		if (!selectedProjectId) {
			toast.error(t("productDetail.selectProject", "Please select a project"));
			return;
		}

		if (needsVariantSelection) {
			toast.error(
				t(
					"productDetail.selectVariantFirst",
					"Please select a variant before shortlisting",
				),
			);
			return;
		}

		setIsSubmitting(true);
		try {
			// A quote needs a vendor the system knows about and a price. Rows the
			// designer started and abandoned are simply not sent.
			const readyQuotes = quotes.filter((q) => q.vendor && q.price.trim());

			const badPrice = readyQuotes.find((q) =>
				Number.isNaN(Number.parseFloat(q.price.replace(/[^0-9.]/g, ""))),
			);
			if (badPrice) {
				toast.error(
					"Enter prices as plain numbers, without a currency symbol. Use 39, not ₹39.",
				);
				setIsSubmitting(false);
				return;
			}

			const result = await addToShortlist(selectedProjectId, {
				productId: product.id,
				variantId: selectedVariant?.id,
				quantity: quantity ? parseInt(quantity, 10) : undefined,
				notes: notes || undefined,
			});

			// Quotes are rows now, created against the entry that was just made.
			// Sequential rather than parallel so a failure part-way names the vendor
			// it failed on rather than reporting an anonymous partial success.
			if (result.success && result.entry?.id) {
				for (const q of readyQuotes) {
					if (!q.vendor) continue;
					// Rupees in the form, paise in the database -- the same conversion
					// the rest of the money in this codebase uses. Doing it here rather
					// than server-side keeps the API honest about its unit.
					const rupees = Number.parseFloat(q.price.replace(/[^0-9.]/g, ""));
					await addQuote(selectedProjectId, result.entry.id, {
						vendorId: q.vendor.id,
						price: Math.round(rupees * 100),
						uom: q.unit,
						leadTimeDays: q.leadTimeDays
							? Number.parseInt(q.leadTimeDays, 10)
							: null,
						tag: q.tag.trim() || null,
					});
				}
			}

			if (result.success) {
				toast.success(
					t("productDetail.addedToShortlist", "Added to project shortlist"),
				);
				onClose();
			} else {
				toast.error(
					result.error ||
						t("productDetail.shortlistError", "Failed to add to shortlist"),
				);
			}
		} catch {
			toast.error(
				t("productDetail.shortlistError", "Failed to add to shortlist"),
			);
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs">
			<FloatPanel
				elevation={3}
				padding={28}
				style={{ maxWidth: 900, width: "100%", margin: "0 16px" }}
			>
				<div className="space-y-6">
					<div>
						<Eyebrow>SHORTLIST FOR PROJECT</Eyebrow>
						<h3
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 22,
								fontWeight: 600,
								color: "var(--color-dashboard-text)",
								marginTop: 4,
							}}
						>
							Shortlist to Project
						</h3>
					</div>

					{needsVariantSelection && (
						<div className="rounded-md bg-[var(--color-dashboard-warning-bg-soft)] border border-[var(--color-dashboard-warning-border)] p-3 text-sm text-[var(--color-dashboard-warning)]">
							{t(
								"productDetail.selectVariantPrompt",
								"This product has variants. Please select a variant before shortlisting.",
							)}
						</div>
					)}

					{/* Project selector */}
					<div className="space-y-2">
						<span
							style={{
								display: "block",
								fontSize: 13,
								fontWeight: 500,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							{t("productDetail.project", "Project")}
						</span>
						{isLoadingProjects ? (
							<Skeleton className="h-10 w-full" />
						) : projects.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								{t(
									"productDetail.noProjects",
									"No projects found. Create a project first.",
								)}
							</p>
						) : (
							<Select
								value={selectedProjectId}
								onValueChange={setSelectedProjectId}
							>
								<SelectTrigger
									style={{
										borderRadius: 7,
										borderColor: "var(--color-border-default)",
										background: "var(--color-dashboard-paper)",
									}}
								>
									<SelectValue
										placeholder={t(
											"productDetail.selectProject",
											"Select a project",
										)}
									/>
								</SelectTrigger>
								<SelectContent>
									{projects.map((p) => (
										<SelectItem key={p.id} value={p.id}>
											{p.title}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>

					{/* Quantity */}
					<div className="space-y-2">
						<span
							style={{
								display: "block",
								fontSize: 13,
								fontWeight: 500,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							{t("productDetail.quantity", "Quantity")} (
							{t("common.optional", "optional")})
						</span>
						<input
							type="number"
							min="1"
							value={quantity}
							onChange={(e) => setQuantity(e.target.value)}
							placeholder="1"
							style={{
								width: "100%",
								padding: "10px 14px",
								border: "1px solid var(--color-border-default)",
								borderRadius: 7,
								background: "var(--color-dashboard-paper)",
								fontSize: 14,
								color: "var(--color-dashboard-text)",
								outline: "none",
							}}
						/>
					</div>

					{/* Notes */}
					<div className="space-y-2">
						<span
							style={{
								display: "block",
								fontSize: 13,
								fontWeight: 500,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							{t("productDetail.notes", "Notes")} (
							{t("common.optional", "optional")})
						</span>
						<textarea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder={t(
								"productDetail.notesPlaceholder",
								"Any notes about this product...",
							)}
							rows={3}
							style={{
								width: "100%",
								padding: "10px 14px",
								border: "1px solid var(--color-border-default)",
								borderRadius: 7,
								background: "var(--color-dashboard-paper)",
								fontSize: 14,
								color: "var(--color-dashboard-text)",
								outline: "none",
								resize: "vertical",
							}}
						/>
					</div>

					{/* Quotes — designer or hired contractor */}
					{canAddQuotes && (
						<div className="space-y-3">
							<span
								style={{
									display: "block",
									fontSize: 13,
									fontWeight: 500,
									color: "var(--color-dashboard-muted-dark)",
								}}
							>
								{t("productDetail.quotes", "Vendor Quotes")} (
								{t("common.optional", "optional")})
							</span>
							<p
								style={{
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
									marginBottom: 12,
								}}
							>
								Add as many as you have. The homeowner picks one, and their
								choice sets the price on the BOQ.
							</p>
							<div className="space-y-3">
								{quotes.map((quote, idx) => (
									<div
										key={quote.key}
										style={{ display: "flex", gap: 8, alignItems: "center" }}
									>
										<VendorTypeahead
											value={quote.vendor}
											onChange={(vendor) => updateQuote(idx, { vendor })}
										/>
										<input
											type="number"
											value={quote.price}
											onChange={(e) =>
												updateQuote(idx, { price: e.target.value })
											}
											placeholder="Price ₹"
											style={{
												width: 90,
												padding: "8px 10px",
												border: "1px solid var(--color-border-default)",
												borderRadius: 6,
												background: "var(--color-dashboard-paper)",
												fontSize: 13,
												color: "var(--color-dashboard-text)",
												outline: "none",
											}}
										/>
										<select
											value={quote.unit}
											onChange={(e) =>
												updateQuote(idx, { unit: e.target.value })
											}
											style={{
												width: 96,
												padding: "8px 6px",
												border: "1px solid var(--color-border-default)",
												borderRadius: 6,
												background: "var(--color-dashboard-paper)",
												fontSize: 13,
												color: "var(--color-dashboard-text)",
												outline: "none",
											}}
										>
											{UNIT_OPTIONS.map((u) => (
												<option key={u} value={u}>
													/{formatUom(u)}
												</option>
											))}
										</select>
										<input
											type="number"
											min="0"
											value={quote.leadTimeDays}
											onChange={(e) =>
												updateQuote(idx, { leadTimeDays: e.target.value })
											}
											placeholder="Days"
											title="Lead time in days"
											style={{
												width: 70,
												padding: "8px 10px",
												border: "1px solid var(--color-border-default)",
												borderRadius: 6,
												background: "var(--color-dashboard-paper)",
												fontSize: 13,
												color: "var(--color-dashboard-text)",
												outline: "none",
											}}
										/>
										<input
											type="text"
											value={quote.tag}
											onChange={(e) =>
												updateQuote(idx, { tag: e.target.value })
											}
											placeholder="Label"
											style={{
												width: 90,
												padding: "8px 10px",
												border: "1px solid var(--color-border-default)",
												borderRadius: 6,
												background: "var(--color-dashboard-paper)",
												fontSize: 13,
												color: "var(--color-dashboard-text)",
												outline: "none",
											}}
										/>
										<button
											type="button"
											onClick={() =>
												setQuotes((prev) =>
													prev.length === 1
														? prev
														: prev.filter((_, i) => i !== idx),
												)
											}
											disabled={quotes.length === 1}
											title="Remove this quote"
											style={{
												border: 0,
												background: "transparent",
												cursor: quotes.length === 1 ? "default" : "pointer",
												color:
													quotes.length === 1
														? "var(--color-dashboard-faint-text)"
														: "var(--color-dashboard-muted)",
												fontSize: 16,
												padding: "0 4px",
											}}
										>
											×
										</button>
									</div>
								))}
								<button
									type="button"
									onClick={() => setQuotes((prev) => [...prev, newRow()])}
									style={{
										border: "1px dashed var(--color-border-default)",
										background: "transparent",
										borderRadius: 6,
										padding: "7px 12px",
										fontSize: 12.5,
										color: "var(--color-dashboard-accent-dark)",
										cursor: "pointer",
									}}
								>
									+ Add another quote
								</button>
							</div>
						</div>
					)}

					{/* Actions */}
					<div className="flex justify-end gap-3 pt-2">
						<StudioButton
							variant="quiet"
							size="sm"
							onClick={onClose}
							disabled={isSubmitting}
						>
							{t("common.cancel", "Cancel")}
						</StudioButton>
						<StudioButton
							variant="primary"
							size="sm"
							onClick={handleSubmit}
							disabled={
								isSubmitting ||
								isLoadingProjects ||
								projects.length === 0 ||
								needsVariantSelection
							}
						>
							{isSubmitting ? (
								t("common.adding", "Adding...")
							) : (
								<>
									<Plus className="mr-1 h-4 w-4" />
									{t("productDetail.addToShortlist", "Add to Shortlist")}
								</>
							)}
						</StudioButton>
					</div>
				</div>
			</FloatPanel>
		</div>
	);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ProductDetailPage(): React.ReactElement {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const [product, setProduct] = useState<CatalogProduct | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
		null,
	);
	const [variantSelections, setVariantSelections] = useState<
		Record<string, string>
	>({});
	const [isInCompare, setIsInCompare] = useState(false);
	const [showShortlistModal, setShowShortlistModal] = useState(false);

	// ---- Data fetching ----
	useEffect(() => {
		async function fetchProduct(): Promise<void> {
			if (!id) return;
			setIsLoading(true);
			try {
				const data = await getCatalogProduct(id);
				setProduct(data);

				if (data) {
					// Check compare status
					const compareIds = getCompareIds();
					setIsInCompare(compareIds.includes(data.id));

					// If variant param in URL, select that variant
					const variantParam = searchParams.get("variant");
					if (variantParam && data.variants) {
						const found = data.variants.find((v) => v.id === variantParam);
						if (found) {
							setSelectedVariant(found);
							// Reconstruct variantSelections from variant attributes
							setVariantSelections(
								Object.fromEntries(
									Object.entries(found.variantAttributes).map(([k, v]) => [
										k,
										String(v),
									]),
								),
							);
						}
					}
				}
			} catch {
				setProduct(null);
			} finally {
				setIsLoading(false);
			}
		}
		fetchProduct();
	}, [id, searchParams]);

	// ---- Variant logic ----
	const variantAttributeKeys = useMemo(() => {
		if (!product?.variants || product.variants.length === 0) return [];
		const keys = new Set<string>();
		for (const v of product.variants) {
			for (const k of Object.keys(v.variantAttributes)) {
				keys.add(k);
			}
		}
		return Array.from(keys);
	}, [product]);

	const variantAttributeValues = useMemo(() => {
		if (!product?.variants) return {} as Record<string, string[]>;
		const map: Record<string, Set<string>> = {};
		for (const v of product.variants) {
			for (const [k, val] of Object.entries(v.variantAttributes)) {
				if (!map[k]) map[k] = new Set();
				map[k].add(String(val));
			}
		}
		return Object.fromEntries(
			Object.entries(map).map(([k, s]) => [k, Array.from(s)]),
		) as Record<string, string[]>;
	}, [product]);

	const handleVariantAttributeChange = useCallback(
		(key: string, value: string) => {
			const next = { ...variantSelections, [key]: value };
			setVariantSelections(next);

			// Find matching variant
			if (product?.variants) {
				const match = product.variants.find((v) =>
					Object.entries(next).every(
						([k, val]) => String(v.variantAttributes[k]) === val,
					),
				);
				if (match) {
					setSelectedVariant(match);
					setSearchParams({ variant: match.id }, { replace: true });
				} else {
					setSelectedVariant(null);
					searchParams.delete("variant");
					setSearchParams(searchParams, { replace: true });
				}
			}
		},
		[variantSelections, product, searchParams, setSearchParams],
	);

	// ---- Compare toggle ----
	function handleCompareToggle(): void {
		if (!product) return;
		const ids = getCompareIds();
		let nextIds: string[];
		if (isInCompare) {
			nextIds = ids.filter((cid) => cid !== product.id);
			setIsInCompare(false);
			toast.success(
				t("productDetail.removedFromCompare", "Removed from compare"),
			);
		} else {
			nextIds = [...ids, product.id];
			setIsInCompare(true);
			toast.success(t("productDetail.addedToCompare", "Added to compare"));
		}
		setCompareIds(nextIds);
		window.dispatchEvent(
			new CustomEvent("catalog-compare-changed", { detail: nextIds }),
		);
	}

	// ---- Price computation ----
	const displayImages = useMemo(() => {
		if (selectedVariant && selectedVariant.images.length > 0) {
			return selectedVariant.images;
		}
		return product?.images ?? [];
	}, [product, selectedVariant]);

	// Price comes from the pricing projection: GST-normalised, carrying its own
	// basis and observation date, and ABSENT when we do not know what a product
	// costs. The previous computation read the `mrp` mirror -- a raw published
	// figure whose GST treatment varies by source -- so two products on this page
	// could differ by ~18% for no reason a customer could see.
	//
	// There is deliberately no fallback to `mrp` when `price` is null. A fallback
	// would put back exactly the number this model exists to stop showing, and
	// would do it precisely on the products we are least certain about.
	const displayPrice = useMemo(() => {
		if (!product?.price) return null;

		// A row for the selected variant beats the product-level one.
		const forVariant = selectedVariant
			? product.prices?.find((p) => p.variantId === selectedVariant.id)
			: undefined;
		const price = forVariant ?? product.price;

		// "From" only when the selection actually spans several amounts. "From"
		// against one price overstates the range; an exact figure against several
		// understates it.
		const distinct = new Set((product.prices ?? []).map((p) => p.amount));
		const spans = !forVariant && distinct.size > 1;

		return { type: spans ? ("from" as const) : ("exact" as const), price };
	}, [product, selectedVariant]);

	// ---- Ordered specifications ----
	const orderedSpecs = useMemo(() => {
		if (!product?.specifications) return [];
		const specEntries = Object.entries(product.specifications);

		// If specTemplate has attributes, use their displayOrder
		if (
			product.specTemplate?.attributes &&
			product.specTemplate.attributes.length > 0
		) {
			const attrMap = new Map(
				product.specTemplate.attributes.map((a) => [a.name, a]),
			);
			return specEntries
				.map(([name, value]) => ({
					name,
					value: String(value),
					unit: attrMap.get(name)?.unit,
					order: attrMap.get(name)?.displayOrder ?? 999,
				}))
				.sort((a, b) => a.order - b.order);
		}

		return specEntries.map(([name, value]) => ({
			name,
			value: String(value),
			unit: undefined,
			order: 0,
		}));
	}, [product]);

	// ---- Render ----
	if (isLoading) return <ProductDetailSkeleton />;
	if (!product) return <ProductNotFound />;

	// No page-level uom label any more: the unit now comes off the price, so the
	// figure and the unit it is quoted in cannot disagree.
	const tierStyle = TIER_STYLES[product.tier] || TIER_STYLES.standard;
	const hasVariants = product.variants && product.variants.length > 0;

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			{/* Back button bar */}
			<div className="mx-auto max-w-5xl mb-6">
				<StudioButton
					variant="quiet"
					size="sm"
					onClick={() => navigate(-1)}
					icon={ArrowLeft}
				>
					{t("common.back", "Back")}
				</StudioButton>
			</div>

			<div className="mx-auto max-w-5xl space-y-8">
				{/* Breadcrumb */}
				<nav className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono uppercase tracking-wider">
					<button
						type="button"
						onClick={() => navigate("/catalog")}
						className="hover:text-foreground transition-colors"
						style={{ background: "transparent", border: 0, cursor: "pointer" }}
					>
						{t("productDetail.home", "Home")}
					</button>
					<ChevronRight className="h-3 w-3" />
					{product.category && (
						<>
							<button
								type="button"
								onClick={() =>
									navigate(`/catalog?categoryId=${product.category?.id}`)
								}
								className="hover:text-foreground transition-colors"
								style={{
									background: "transparent",
									border: 0,
									cursor: "pointer",
								}}
							>
								{product.category.name}
							</button>
							<ChevronRight className="h-3 w-3" />
						</>
					)}
					<span className="text-[var(--color-dashboard-accent-dark)] font-semibold truncate max-w-50">
						{product.name}
					</span>
				</nav>

				{/* Two-column layout */}
				<div className="grid grid-cols-1 gap-8 md:grid-cols-2">
					{/* Left: Image Gallery */}
					<ImageGallery images={displayImages} fallbackAlt={product.name} />

					{/* Right: Product Info */}
					<div className="space-y-6">
						<div>
							{/* Brand */}
							{product.brand && (
								<button
									type="button"
									onClick={() =>
										navigate(`/catalog?brands=${product.brand?.id}`)
									}
									className="text-xs font-semibold uppercase tracking-wider text-[var(--color-dashboard-accent-dark)] hover:text-[var(--color-dashboard-accent-dark)]/80 transition-colors"
									style={{
										background: "transparent",
										border: 0,
										cursor: "pointer",
										display: "block",
									}}
								>
									{product.brand.name}
								</button>
							)}

							{/* Product name */}
							<h1
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 32,
									lineHeight: 1.15,
									color: "var(--color-dashboard-text)",
									marginTop: 6,
								}}
							>
								{product.name}
							</h1>

							{/* Quality tier badge */}
							<span
								className={cn(
									"inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider mt-3",
									tierStyle.className,
								)}
							>
								{tierStyle.label}
							</span>
						</div>

						{/* Price section */}
						<div className="space-y-1">
							{displayPrice ? (
								<>
									<div className="flex items-baseline gap-1">
										<IndianRupee className="h-5 w-5 text-dashboard-text" />
										<span
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 600,
												fontSize: 28,
												color: "var(--color-dashboard-text)",
											}}
										>
											{displayPrice.type === "from"
												? `From ${formatPricePaisa(displayPrice.price.amount)}`
												: formatPricePaisa(displayPrice.price.amount)}
										</span>
										<span
											style={{
												fontSize: 13,
												color: "var(--color-dashboard-muted)",
												fontFamily: "var(--font-body)",
											}}
										>
											/{formatUom(displayPrice.price.uom)}
										</span>
									</div>
									{/*
									 * The number alone is not the claim. A price means something
									 * different depending on whether it is an MRP or a trade
									 * price, and how long ago anyone checked -- so the basis and
									 * the date travel with it rather than being available
									 * somewhere else.
									 */}
									<p
										style={{
											fontSize: 13,
											color: "var(--color-dashboard-muted)",
											fontFamily: "var(--font-body)",
										}}
									>
										{displayPrice.price.basisLabel}
										{" · "}
										{t("productDetail.priceObserved", "checked")}{" "}
										{formatCalendarDate(displayPrice.price.observedAt, {
											month: "short",
											year: "numeric",
										})}
									</p>
									{displayPrice.price.isStale && (
										<p
											style={{
												fontSize: 13,
												fontFamily: "var(--font-body)",
												color: "var(--color-dashboard-pill-awaiting)",
											}}
										>
											{t(
												"productDetail.priceStale",
												"This price is past its refresh date and may have moved. Confirm before ordering.",
											)}
										</p>
									)}
								</>
							) : (
								/*
								 * A first-class state, not ₹0 (acceptance V12). We hold prices
								 * we cannot normalise -- a source that does not say whether GST
								 * is in or out -- and printing zero for those would be a
								 * confident claim that the product is free. Saying nothing is
								 * the honest answer and the one a customer can act on.
								 */
								<p
									style={{
										fontSize: 13,
										color: "var(--color-dashboard-muted)",
										fontFamily: "var(--font-body)",
									}}
								>
									{t("productDetail.priceUnavailable", "Price on request")}
								</p>
							)}
						</div>

						{/* Description */}
						{product.description && (
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									lineHeight: 1.6,
									color: "var(--color-dashboard-muted-dark)",
								}}
							>
								{product.description}
							</p>
						)}

						{/* Variant selector */}
						{hasVariants && variantAttributeKeys.length > 0 && (
							<div className="space-y-4 rounded-xl border border-dashboard-border bg-dashboard-bg p-5">
								<h3
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 600,
										fontSize: 14.5,
										color: "var(--color-dashboard-text)",
									}}
								>
									{t("productDetail.selectVariant", "Select Variant")}
								</h3>
								{variantAttributeKeys.map((key) => {
									const values = variantAttributeValues[key] || [];
									return (
										<div key={key} className="space-y-2">
											<span
												style={{
													display: "block",
													fontSize: 12,
													fontWeight: 500,
													color: "var(--color-dashboard-muted)",
													textTransform: "capitalize",
												}}
											>
												{key}
											</span>
											<div className="flex flex-wrap gap-2">
												{values.map((val) => (
													<button
														type="button"
														key={val}
														onClick={() =>
															handleVariantAttributeChange(key, val)
														}
														style={{
															padding: "8px 14px",
															border: "1.5px solid",
															borderColor:
																variantSelections[key] === val
																	? "var(--color-dashboard-accent)"
																	: "var(--color-border-default)",
															background:
																variantSelections[key] === val
																	? "var(--color-dashboard-accent)"
																	: "var(--color-dashboard-paper)",
															color:
																variantSelections[key] === val
																	? "var(--color-dashboard-on-fill)"
																	: "var(--color-dashboard-text)",
															borderRadius: 6,
															fontSize: 13,
															fontWeight:
																variantSelections[key] === val ? 600 : 500,
															cursor: "pointer",
															transition: "all 150ms ease",
														}}
													>
														{val}
													</button>
												))}
											</div>
										</div>
									);
								})}
							</div>
						)}

						{/* Action buttons */}
						<div className="flex flex-wrap gap-3 pt-2">
							<StudioButton
								variant={isInCompare ? "secondary" : "quiet"}
								onClick={handleCompareToggle}
								icon={isInCompare ? Check : GitCompare}
							>
								{isInCompare
									? t("productDetail.inCompare", "In Compare")
									: t("productDetail.addToCompare", "Add to Compare")}
							</StudioButton>

							<StudioButton
								variant="primary"
								onClick={() => setShowShortlistModal(true)}
								icon={ShoppingCart}
							>
								{t("productDetail.shortlistToProject", "Shortlist to Project")}
							</StudioButton>
						</div>
					</div>
				</div>

				{/* Specifications */}
				{orderedSpecs.length > 0 && (
					<FloatPanel elevation={1} padding={28}>
						<div className="space-y-6">
							<div>
								<Eyebrow>TECHNICAL DETAILS</Eyebrow>
								<h3
									style={{
										fontFamily: "var(--font-display)",
										fontSize: 20,
										fontWeight: 600,
										color: "var(--color-dashboard-text)",
										marginTop: 4,
									}}
								>
									{t("productDetail.specifications", "Specifications")}
								</h3>
							</div>
							<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
								{orderedSpecs.map((spec) => (
									<div
										key={spec.name}
										style={{
											background: "var(--color-dashboard-paper)",
											border: "1px solid var(--color-dashboard-border)",
											borderRadius: 8,
											padding: "16px 18px",
											display: "flex",
											flexDirection: "column",
											gap: 6,
										}}
									>
										<span
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 9,
												letterSpacing: "0.1em",
												textTransform: "uppercase",
												color: "var(--color-dashboard-muted)",
											}}
										>
											{spec.name}
										</span>
										<span
											style={{
												fontSize: 15,
												fontWeight: 600,
												color: "var(--color-dashboard-text)",
												fontFamily: "var(--font-body)",
												lineHeight: 1.3,
											}}
										>
											{spec.value}
											{spec.unit ? (
												<span
													style={{
														fontWeight: 400,
														color: "var(--color-dashboard-muted)",
														marginLeft: 4,
													}}
												>
													{spec.unit}
												</span>
											) : null}
										</span>
									</div>
								))}
							</div>
						</div>
					</FloatPanel>
				)}
			</div>

			{/* Shortlist modal */}
			{showShortlistModal && (
				<ShortlistModal
					product={product}
					selectedVariant={selectedVariant}
					onClose={() => setShowShortlistModal(false)}
				/>
			)}
		</div>
	);
}
